import React, { useCallback, useMemo } from 'react';
import { FiservCardInterface, FiservCardStatusEnum, OptionKey } from '@common-fe/common-fe';

import ModalWrapper from '@/components/wrappers/ModalWrapper';
import { useChangeCardStatus } from '@/modules/transaction/Cards/queries/useChangeCardStatus.query';

import LockCardConfirmDialog from './components/LockCardConfirmDialog';
import UnlockCardConfirmDialog from './components/UnlockCardConfirmDialog';

interface Props {
  card?: FiservCardInterface;
  visible: boolean;
  onCancel: () => void;
  onSuccess?: (nextStatus: 'locked' | 'unlocked') => void;
}

const LOCKED_STATUSES = [FiservCardStatusEnum.FROZEN, FiservCardStatusEnum.PENDING_FROZEN];

export const LockUnlockCardModal: React.FC<Props> = ({ card, visible, onCancel, onSuccess }) => {
  const isCardLocked = useMemo(() => LOCKED_STATUSES.includes(card?.status as FiservCardStatusEnum), [card?.status]);
  const { mutateAsync: changeStatusMutation, isPending: isLoading } = useChangeCardStatus();

  const handleUnlock = useCallback(async () => {
    if (!card) return;
    await changeStatusMutation({ id: card.id, status: FiservCardStatusEnum.ACTIVE, isEmployeeUnlockForbidden: false });
    onCancel();
    onSuccess?.('unlocked');
  }, [card, changeStatusMutation, onCancel, onSuccess]);

  const handleLock = useCallback(async (params: { lockReason: OptionKey; isEmployeeUnlockForbidden: boolean }) => {
    if (!card) return;
    await changeStatusMutation({
      id: card.id,
      status: FiservCardStatusEnum.FROZEN,
      lockReason: params.lockReason,
      isEmployeeUnlockForbidden: params.isEmployeeUnlockForbidden,
    });
    onCancel();
    onSuccess?.('locked');
  }, [card, changeStatusMutation, onCancel, onSuccess]);

  return (
    <ModalWrapper
      testId="lock-unlock-card-modal"
      visible={visible}
      onSetVisible={onCancel}
      title={isCardLocked ? undefined : 'Confirm Card Lock'}
    >
      {isCardLocked ? (
        <UnlockCardConfirmDialog
          isLoading={isLoading}
          onCancel={onCancel}
          onConfirm={handleUnlock}
        />
      ) : (
        <LockCardConfirmDialog
          isLoading={isLoading}
          onCancel={onCancel}
          onConfirm={handleLock}
        />
      )}
    </ModalWrapper>
  );
};

export default LockUnlockCardModal;
